import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaTimes } from 'react-icons/fa';

import productosService from '../../services/productosService';
import ProductImage from '../products/ProductImage';
import analyticsService, { COMMERCE_SOURCES } from '../../services/analyticsService';
import { rutaProducto } from '../../utils/productUrl';

/**
 * Buscador del encabezado, con autocompletado.
 *
 * El catálogo ya tenía un buscador dentro de los filtros y funciona, pero
 * queda escondido: hay que entrar a la tienda y abrir el panel para verlo. Este
 * está disponible desde cualquier página, que es donde la gente lo espera.
 *
 * Sigue el patrón ARIA de combobox: el input anuncia la lista, cada opción
 * tiene su id, y la opción activa se comunica con aria-activedescendant en vez
 * de moverle el foco — así el usuario puede seguir escribiendo mientras navega
 * con las flechas.
 */

// Rebote corto: son los resultados que el usuario está mirando, tienen que
// sentirse inmediatos. El reporte a analítica usa su propio tiempo, más largo
// (ver trackSearchSettled).
const DEBOUNCE_MS = 250;
const MAX_SUGERENCIAS = 6;
const MIN_CARACTERES = 2;

const formatearPrecio = valor =>
  typeof valor === 'number' && Number.isFinite(valor) ? `$${valor.toLocaleString()}` : null;

export default function HeaderSearch({ onNavigate, autoFocus = false, className = '' }) {
  const navigate = useNavigate();
  const listboxId = useId();

  const [termino, setTermino] = useState('');
  const [sugerencias, setSugerencias] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [activo, setActivo] = useState(-1);
  const [total, setTotal] = useState(0);

  const contenedorRef = useRef(null);
  const inputRef = useRef(null);
  // Sirve para descartar respuestas de peticiones que ya quedaron obsoletas:
  // sin esto, una consulta lenta puede pisar los resultados de otra posterior.
  const peticionRef = useRef(0);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  // Buscar con rebote
  useEffect(() => {
    const consulta = termino.trim();

    if (consulta.length < MIN_CARACTERES) {
      setSugerencias([]);
      setTotal(0);
      setCargando(false);
      return;
    }

    setCargando(true);
    const idPeticion = ++peticionRef.current;

    const timer = setTimeout(async () => {
      try {
        const data = await productosService.getProductos({
          search: consulta,
          limit: MAX_SUGERENCIAS,
        });
        if (idPeticion !== peticionRef.current) return;

        setSugerencias(data.productos || []);
        setTotal(data.pagination?.total || 0);
        setActivo(-1);
        setAbierto(true);
        analyticsService.trackSearchSettled(consulta, data.pagination?.total || 0);
      } catch {
        if (idPeticion !== peticionRef.current) return;
        setSugerencias([]);
        setTotal(0);
      } finally {
        if (idPeticion === peticionRef.current) setCargando(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [termino]);

  // Cerrar al hacer clic fuera
  useEffect(() => {
    if (!abierto) return;
    const alHacerClic = e => {
      if (!contenedorRef.current?.contains(e.target)) setAbierto(false);
    };
    document.addEventListener('mousedown', alHacerClic);
    return () => document.removeEventListener('mousedown', alHacerClic);
  }, [abierto]);

  const cerrar = useCallback(() => {
    setAbierto(false);
    setActivo(-1);
  }, []);

  const limpiar = useCallback(() => {
    setTermino('');
    setSugerencias([]);
    setTotal(0);
    cerrar();
    analyticsService.cancelPendingSearch();
    inputRef.current?.focus();
  }, [cerrar]);

  const irAProducto = useCallback(
    producto => {
      // clicked_result=true: por fin se puede saber qué búsquedas terminan en
      // un producto concreto y cuáles se quedan sin encontrar nada.
      analyticsService.trackSearchSettled(termino.trim(), total, true);
      cerrar();
      setTermino('');
      onNavigate?.();
      navigate(rutaProducto(producto), { state: { source: COMMERCE_SOURCES.BUSCADOR } });
    },
    [cerrar, navigate, onNavigate, termino, total]
  );

  const verTodos = useCallback(() => {
    const consulta = termino.trim();
    if (!consulta) return;
    analyticsService.trackSearchSettled(consulta, total, true);
    cerrar();
    onNavigate?.();
    navigate(`/productos?search=${encodeURIComponent(consulta)}`);
  }, [cerrar, navigate, onNavigate, termino, total]);

  const alTeclear = e => {
    if (e.key === 'Escape') {
      if (abierto) cerrar();
      else limpiar();
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (activo >= 0 && sugerencias[activo]) irAProducto(sugerencias[activo]);
      else verTodos();
      return;
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!sugerencias.length) return;
      if (!abierto) {
        setAbierto(true);
        return;
      }
      const paso = e.key === 'ArrowDown' ? 1 : -1;
      setActivo(prev => {
        const siguiente = prev + paso;
        if (siguiente < -1) return sugerencias.length - 1;
        if (siguiente >= sugerencias.length) return -1;
        return siguiente;
      });
    }
  };

  const sinResultados =
    abierto && !cargando && termino.trim().length >= MIN_CARACTERES && sugerencias.length === 0;

  return (
    <div ref={contenedorRef} className={`relative ${className}`}>
      <div className="relative">
        <FaSearch
          aria-hidden="true"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-df-muted text-sm pointer-events-none"
        />
        <input
          ref={inputRef}
          type="search"
          value={termino}
          onChange={e => setTermino(e.target.value)}
          onKeyDown={alTeclear}
          onFocus={() => sugerencias.length && setAbierto(true)}
          placeholder="Buscar juegos..."
          aria-label="Buscar juegos"
          role="combobox"
          aria-expanded={abierto}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activo >= 0 ? `${listboxId}-opcion-${activo}` : undefined}
          autoComplete="off"
          className="w-full bg-df-surface2 border border-df-border rounded-lg pl-9 pr-9 py-2 text-sm text-df-text placeholder:text-df-muted focus:outline-none focus:ring-2 focus:ring-df-accent focus:border-transparent transition-colors [&::-webkit-search-cancel-button]:hidden"
        />
        {termino && (
          <button
            type="button"
            onClick={limpiar}
            aria-label="Limpiar búsqueda"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-df-muted hover:text-df-text rounded transition-colors"
          >
            <FaTimes className="text-xs" />
          </button>
        )}
      </div>

      {abierto && (sugerencias.length > 0 || sinResultados) && (
        <div className="absolute z-50 mt-2 w-full min-w-[20rem] right-0 bg-df-surface border border-df-border rounded-xl shadow-2xl shadow-black/50 overflow-hidden">
          <ul id={listboxId} role="listbox" aria-label="Sugerencias de productos" className="max-h-96 overflow-y-auto">
            {sugerencias.map((producto, i) => {
              const precio =
                formatearPrecio(producto.Precio_Oferta) || formatearPrecio(producto.Precio_Publico);
              return (
                <li
                  key={producto.id}
                  id={`${listboxId}-opcion-${i}`}
                  role="option"
                  aria-selected={i === activo}
                >
                  <button
                    type="button"
                    // onMouseDown y no onClick: el clic fuera cierra la lista en
                    // mousedown, y para entonces el botón ya no existiría.
                    onMouseDown={e => {
                      e.preventDefault();
                      irAProducto(producto);
                    }}
                    onMouseEnter={() => setActivo(i)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                      i === activo ? 'bg-df-accent/15' : 'hover:bg-df-surface2'
                    }`}
                  >
                    <ProductImage
                      src={producto.Imagen}
                      alt=""
                      className="h-10 w-10 flex-shrink-0 rounded p-0.5"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm text-df-text truncate">{producto.Juego}</span>
                      {producto.marca && (
                        <span className="block text-xs text-df-muted truncate">{producto.marca}</span>
                      )}
                    </span>
                    {precio && (
                      <span className="text-sm font-semibold text-df-gold whitespace-nowrap">
                        {precio}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>

          {sinResultados && (
            <p className="px-4 py-6 text-sm text-df-muted text-center">
              No encontramos juegos para <span className="text-df-text">«{termino.trim()}»</span>
            </p>
          )}

          {sugerencias.length > 0 && (
            <button
              type="button"
              onMouseDown={e => {
                e.preventDefault();
                verTodos();
              }}
              className="w-full px-4 py-2.5 text-xs font-medium text-df-accent hover:text-df-accent-hover hover:bg-df-surface2 border-t border-df-border transition-colors text-left"
            >
              Ver {total > MAX_SUGERENCIAS ? `los ${total} resultados` : 'todos los resultados'} para
              «{termino.trim()}»
            </button>
          )}
        </div>
      )}

      {/* Anuncio para lectores de pantalla: sin esto, quien no ve la lista no
          se entera de que aparecieron resultados. */}
      <span className="sr-only" role="status" aria-live="polite">
        {cargando
          ? 'Buscando...'
          : abierto && sugerencias.length > 0
            ? `${sugerencias.length} sugerencias disponibles`
            : ''}
      </span>
    </div>
  );
}
